const OVERLAP_TOLERANCE_PX = 0.75;
const MIN_HORIZONTAL_TEXT_OVERLAP_PX = 1.25;
const MIN_VERTICAL_TEXT_OVERLAP_PX = 1.5;
const MAX_VERTICAL_TEXT_OVERLAP_PX = 3;
const GUTTER_OVERFLOW_TOLERANCE_PX = 1.25;
const TITLE_SAFE_INSET_RATIO = 0.1;
const TITLE_SAFE_OVERFLOW_TOLERANCE_PX = 1.25;

const rectWidth = (rect) =>
  Number.isFinite(rect?.width) ? rect.width : rect.right - rect.left;

const rectHeight = (rect) =>
  Number.isFinite(rect?.height) ? rect.height : rect.bottom - rect.top;

const isRoleNameTextOwner = (owner) =>
  owner?.classList?.contains?.("roleName_td") ||
  owner?.classList?.contains?.("roleName-ragged-extra-name") ||
  owner?.classList?.contains?.("roleName_line");

export const rectanglesOverlap = (
  first,
  second,
  tolerance = OVERLAP_TOLERANCE_PX,
) =>
  Math.min(first.right, second.right) - Math.max(first.left, second.left) >
    tolerance &&
  Math.min(first.bottom, second.bottom) - Math.max(first.top, second.top) >
    tolerance;

export const hasOverlappingTextRects = (
  entries,
  tolerance = OVERLAP_TOLERANCE_PX,
  measurementScale = 1,
) => {
  const scale =
    Number.isFinite(measurementScale) && measurementScale > 0
      ? measurementScale
      : 1;
  const displayedTolerance = tolerance * scale;
  const sorted = entries
    .filter(
      ({ rect }) =>
        rect &&
        rectWidth(rect) > displayedTolerance &&
        rectHeight(rect) > displayedTolerance,
    )
    .sort((a, b) => a.rect.top - b.rect.top || a.rect.left - b.rect.left);

  for (let firstIndex = 0; firstIndex < sorted.length; firstIndex += 1) {
    const first = sorted[firstIndex];

    for (
      let secondIndex = firstIndex + 1;
      secondIndex < sorted.length;
      secondIndex += 1
    ) {
      const second = sorted[secondIndex];

      if (second.rect.top >= first.rect.bottom - displayedTolerance) break;

      // A browser Range reports the complete typographic box rather than the
      // visible glyph pixels. Fragments and wrapped lines belonging to one text
      // item can therefore intersect slightly even though the text is laid out
      // correctly. Only separate credit items can represent a layout collision.
      if (first.owner === second.owner) continue;

      // Range rectangles include browser-specific ascender and descender space.
      // For most block types, require the owning layout boxes to intersect so
      // touching rows do not produce false positives. Role + Name is the
      // exception: a tight line-height can let visible ascenders/descenders
      // escape otherwise adjacent row boxes (for example, a trailing "y"
      // colliding with the next name). The stricter text-overlap tolerance
      // below still filters ordinary font-box contact for those rows.
      if (first.ownerRect && second.ownerRect) {
        const ownerVerticalOverlap =
          (Math.min(first.ownerRect.bottom, second.ownerRect.bottom) -
            Math.max(first.ownerRect.top, second.ownerRect.top)) /
          scale;

        const isRoleNameRowPair =
          isRoleNameTextOwner(first.owner) &&
          isRoleNameTextOwner(second.owner);
        const isSameRoleNameGroup =
          first.roleNameGroup &&
          first.roleNameGroup === second.roleNameGroup;

        // Additional names belonging to one role are intentionally stacked as
        // separate text owners. Their Range boxes can cross at tight line
        // heights even though their layout boxes only touch. Keep the special
        // cross-row role/name check for neighboring credit groups, but do not
        // treat this expected within-group font-box contact as a collision.
        if (
          ownerVerticalOverlap <= tolerance &&
          (!isRoleNameRowPair || isSameRoleNameGroup)
        ) {
          continue;
        }
      }

      // Compare in canonical project pixels rather than display pixels. The
      // editor scales its 800px render layout to fit the available viewport;
      // normalizing here keeps warnings identical at every preview size.
      const horizontalOverlap =
        (Math.min(first.rect.right, second.rect.right) -
          Math.max(first.rect.left, second.rect.left)) /
        scale;
      const verticalOverlap =
        (Math.min(first.rect.bottom, second.rect.bottom) -
          Math.max(first.rect.top, second.rect.top)) /
        scale;
      const smallerTextHeight =
        Math.min(rectHeight(first.rect), rectHeight(second.rect)) / scale;

      // Scale the allowance with the canonical text height. This absorbs font
      // ascender/descender boxes and sub-pixel preview scaling while still
      // catching same-row role/name collisions, whose vertical overlap is
      // normally close to the full text height.
      const horizontalTolerance = Math.max(
        tolerance,
        Math.min(
          2,
          Math.max(
            MIN_HORIZONTAL_TEXT_OVERLAP_PX,
            smallerTextHeight * 0.1,
          ),
        ),
      );
      const verticalTolerance = Math.max(
        tolerance,
        Math.min(
          MAX_VERTICAL_TEXT_OVERLAP_PX,
          Math.max(MIN_VERTICAL_TEXT_OVERLAP_PX, smallerTextHeight * 0.2),
        ),
      );

      if (
        horizontalOverlap > horizontalTolerance &&
        verticalOverlap > verticalTolerance
      ) {
        return true;
      }
    }
  }

  return false;
};

export const hasTextOutsideRoleNameGutter = (
  entries,
  tolerance = GUTTER_OVERFLOW_TOLERANCE_PX,
  measurementScale = 1,
) => {
  const scale =
    Number.isFinite(measurementScale) && measurementScale > 0
      ? measurementScale
      : 1;
  const displayedTolerance = tolerance * scale;

  return entries.some(
    ({ owner, ownerRect, ownerPaddingLeft = 0, ownerPaddingRight = 0, rect }) => {
      if (
        !owner?.classList?.contains?.("roleName_td") ||
        !ownerRect ||
        !rect ||
        rectWidth(rect) <= displayedTolerance ||
        rectHeight(rect) <= displayedTolerance
      ) {
        return false;
      }

      const contentLeft = ownerRect.left + ownerPaddingLeft * scale;
      const contentRight = ownerRect.right - ownerPaddingRight * scale;

      return (
        contentLeft - rect.left > displayedTolerance ||
        rect.right - contentRight > displayedTolerance
      );
    },
  );
};

export const getHorizontalTitleSafeBounds = (previewElement) => {
  const frameElement =
    previewElement?.closest?.(".scrollFrame") || previewElement;
  const frameRect = frameElement?.getBoundingClientRect?.();
  const width = frameRect ? rectWidth(frameRect) : 0;

  if (!frameRect || !Number.isFinite(width) || width <= 0) return null;

  const inset = width * TITLE_SAFE_INSET_RATIO;
  return {
    left: frameRect.left + inset,
    right: frameRect.right - inset,
  };
};

export const hasTextOutsideHorizontalTitleSafe = (
  entries,
  bounds,
  tolerance = TITLE_SAFE_OVERFLOW_TOLERANCE_PX,
  measurementScale = 1,
) => {
  if (!bounds) return false;

  const scale =
    Number.isFinite(measurementScale) && measurementScale > 0
      ? measurementScale
      : 1;
  const displayedTolerance = tolerance * scale;

  return entries.some(
    ({ rect }) =>
      rect &&
      rectWidth(rect) > displayedTolerance &&
      rectHeight(rect) > displayedTolerance &&
      (bounds.left - rect.left > displayedTolerance ||
        rect.right - bounds.right > displayedTolerance),
  );
};

const isTransparent = (color) =>
  color === "transparent" ||
  /^rgba\([^)]*,\s*0(?:\.0+)?\s*\)$/i.test(color || "");

const isVisibleTextNode = (node, blockElement) => {
  if (isTransparent(window.getComputedStyle(node.parentElement).color)) {
    return false;
  }

  let element = node.parentElement;

  while (element && element !== blockElement.parentElement) {
    const style = window.getComputedStyle(element);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      Number(style.opacity) === 0
    ) {
      return false;
    }

    if (element === blockElement) break;
    element = element.parentElement;
  }

  return true;
};

const collectTextRects = (blockElement) => {
  if (!document.createRange || !document.createTreeWalker) return [];

  const entries = [];
  const ownerRects = new WeakMap();
  const ownerPaddings = new WeakMap();
  const walker = document.createTreeWalker(
    blockElement,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT;

        const owner = node.parentElement?.closest?.(".FontSIzeScale");
        if (
          !owner ||
          !blockElement.contains(owner) ||
          !isVisibleTextNode(node, blockElement)
        ) {
          return NodeFilter.FILTER_REJECT;
        }

        return NodeFilter.FILTER_ACCEPT;
      },
    },
  );

  let textNode = walker.nextNode();
  while (textNode) {
    const owner = textNode.parentElement.closest(".FontSIzeScale");
    let ownerRect = ownerRects.get(owner);
    if (!ownerRect) {
      ownerRect = owner.getBoundingClientRect();
      ownerRects.set(owner, ownerRect);
    }
    let ownerPadding = ownerPaddings.get(owner);
    if (!ownerPadding) {
      const ownerStyle = window.getComputedStyle(owner);
      ownerPadding = {
        left: parseFloat(ownerStyle.paddingLeft) || 0,
        right: parseFloat(ownerStyle.paddingRight) || 0,
      };
      ownerPaddings.set(owner, ownerPadding);
    }
    const range = document.createRange();
    range.selectNodeContents(textNode);

    Array.from(range.getClientRects?.() || []).forEach((rect) => {
      entries.push({
        owner,
        roleNameGroup:
          owner.dataset?.roleNameGroupId ||
          owner.closest?.("[data-role-name-group]") ||
          null,
        ownerRect,
        ownerPaddingLeft: ownerPadding.left,
        ownerPaddingRight: ownerPadding.right,
        rect,
      });
    });

    range.detach?.();
    textNode = walker.nextNode();
  }

  return entries;
};

export const getPreviewMeasurementScale = (previewElement) => {
  const zoomWrapper = previewElement?.querySelector?.(
    "#preview-zoom-wrapper",
  );
  if (!zoomWrapper) return 1;

  const logicalWidth = zoomWrapper.offsetWidth || zoomWrapper.clientWidth;
  const displayedRect = zoomWrapper.getBoundingClientRect?.();
  const displayedWidth = displayedRect ? rectWidth(displayedRect) : 0;
  const scale = displayedWidth / logicalWidth;

  return Number.isFinite(scale) && scale > 0 ? scale : 1;
};

export const detectCreditBlockGeometryWarnings = (previewElement) => {
  const overlappingBlockIndexes = new Set();
  const gutterOverflowingBlockIndexes = new Set();
  const titleSafeOverflowingBlockIndexes = new Set();
  if (!previewElement) {
    return {
      overlappingBlockIndexes,
      gutterOverflowingBlockIndexes,
      titleSafeOverflowingBlockIndexes,
    };
  }
  const measurementScale = getPreviewMeasurementScale(previewElement);
  const titleSafeBounds = getHorizontalTitleSafeBounds(previewElement);

  previewElement
    .querySelectorAll("[data-credit-block-index]")
    .forEach((blockElement) => {
      const index = Number(blockElement.dataset.creditBlockIndex);
      if (!Number.isInteger(index)) return;
      const textRects = collectTextRects(blockElement);

      if (
        hasOverlappingTextRects(
          textRects,
          OVERLAP_TOLERANCE_PX,
          measurementScale,
        )
      ) {
        overlappingBlockIndexes.add(index);
      }

      if (
        hasTextOutsideRoleNameGutter(
          textRects,
          GUTTER_OVERFLOW_TOLERANCE_PX,
          measurementScale,
        )
      ) {
        gutterOverflowingBlockIndexes.add(index);
      }

      if (
        hasTextOutsideHorizontalTitleSafe(
          textRects,
          titleSafeBounds,
          TITLE_SAFE_OVERFLOW_TOLERANCE_PX,
          measurementScale,
        )
      ) {
        titleSafeOverflowingBlockIndexes.add(index);
      }
    });

  return {
    overlappingBlockIndexes,
    gutterOverflowingBlockIndexes,
    titleSafeOverflowingBlockIndexes,
  };
};

export const detectOverlappingBlockIndexes = (previewElement) =>
  detectCreditBlockGeometryWarnings(previewElement).overlappingBlockIndexes;
